'use client';

import { useEffect, useState } from 'react'; // useState kept for signingOut
import { createPortal } from 'react-dom';
import { activityBus } from '@/lib/http/activity-bus';
import { resetAppNavSession } from '@/lib/app-nav-state';
import { SESSION_ACTIVITY_EVENTS } from '@/lib/session/constants';
import { useSession } from '@/lib/session/SessionProvider';
import { Button } from '@tranquility-ai/theme-components/button';
import { unwatchToastLayer, watchToastLayer, TOAST_Z_CLASS, TOAST_Z_INDEX } from '@/components/ui/toast-layer';

function redirectLocalSignOut(): void {
    resetAppNavSession(window.location.pathname || '/');
    window.location.replace('/api/sign-out?scope=local');
}

/**
 * Records user activity (including media playback) and renders the idle-timeout
 * alert dialog when the session enters the warning window before sign-out.
 */
export function IdleTimeoutManager() {
    const { policy, decision, extendSession, extendPending, extendError, lastActivityAt } = useSession();
    const [signingOut, setSigningOut] = useState(false);
    const [now, setNow] = useState(() => Date.now());
    const [layer, setLayer] = useState<HTMLElement | null>(null);
    const idleLimitMs = policy.idleMinutes * 60 * 1000;
    const inactiveSeconds = Math.max(0, Math.floor((now - lastActivityAt) / 1000));
    const remainingSeconds = decision.kind === 'warn'
        ? Math.max(0, Math.ceil((idleLimitMs - (now - lastActivityAt)) / 1000))
        : 0;

    useEffect(() => {
        const listeners = SESSION_ACTIVITY_EVENTS.map((eventName) => {
            const onActivity = () => {
                activityBus.emit();
            };

            window.addEventListener(eventName, onActivity, true);
            return { eventName, onActivity };
        });

        return () => {
            listeners.forEach(({ eventName, onActivity }) => {
                window.removeEventListener(eventName, onActivity, true);
            });
        };
    }, []);

    useEffect(() => {
        if (decision.kind !== 'warn' || signingOut || extendPending || extendError) {
            return;
        }

        setNow(Date.now());
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, [decision.kind, signingOut, extendPending, extendError]);

    // Guard on extendPending — the request is in flight and the server may be
    // refreshing the cookie. Bounded by the 10s AbortSignal.timeout in
    // SessionProvider, so it always resolves.
    //
    // extendError deliberately does NOT suppress the `expired` branch. It only
    // auto-clears once the session is healthy again, so an extend that fails after
    // the idle deadline would otherwise suppress the sign-out indefinitely. Past the
    // deadline there is nothing left to preserve: /api/session/extend enforces the
    // same limit server-side and would refuse the retry. Sign out rather than strand
    // the user in a session that is already over its limit.
    //
    // It is still honoured in the `warn` branch below, where the session genuinely
    // may still be valid and the user deserves a retry before being signed out.
    useEffect(() => {
        // Idle timeout is automatic and scoped to this browser — use a local
        // sign-out so the user isn't GlobalSignOut'd off their other devices.
        if (decision.kind === 'expired' && !signingOut && !extendPending) {
            setSigningOut(true);
            redirectLocalSignOut();
            return;
        }

        if (
            decision.kind === 'warn'
            && remainingSeconds <= 0
            && !signingOut
            && !extendPending
            && !extendError
        ) {
            setSigningOut(true);
            redirectLocalSignOut();
        }
    }, [decision, remainingSeconds, signingOut, extendPending, extendError]);

    const showBanner = !signingOut && (decision.kind === 'warn' || extendPending || extendError);

    useEffect(() => {
        if (!showBanner) {
            setLayer(null);
            return;
        }
        setLayer(watchToastLayer());
        return () => {
            unwatchToastLayer();
        };
    }, [showBanner]);

    // Conceal the app as soon as idle expires, so case data is not left on screen
    // while the local sign-out navigation runs. Rendered directly rather than through
    // the toast portal: the lock must not depend on the toast layer being mounted.
    //
    // The z-index has to beat every case-data layer, including the toast layer
    // (TOAST_Z_INDEX, 1e8) and the media-capture / external-link modals
    // (z-[99999999]) — anything lower and evidence stays visible through the lock.
    // Max int32 keeps this the top layer by construction, so a new modal cannot
    // quietly slip above it.
    if (decision.kind === 'expired' || signingOut) {
        return (
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="session-lock-title"
                className="fixed inset-0 flex items-center justify-center bg-slate-950 text-slate-100"
                style={{ zIndex: 2147483647 }}
            >
                <div className="max-w-md px-6 text-center">
                    <h1 id="session-lock-title" className="text-xl font-semibold">
                        Session locked
                    </h1>
                    <p className="mt-3 text-sm text-slate-300">
                        You were inactive for {policy.idleMinutes} minutes. Sign in again to continue.
                    </p>
                </div>
            </div>
        );
    }

    if (!showBanner || !layer) return null;

    const handleStaySignedIn = () => {
        // extendSession never throws — errors are surfaced via extendError in context.
        // Calling it immediately sets extendPending=true, suppressing any expired redirect.
        void extendSession();
    };

    const bodyContent = () => {
        if (extendError) {
            return (
                <p className="text-red-600">
                    Could not extend your session. Please try again or sign out.
                </p>
            );
        }
        if (extendPending) {
            return <p className="text-gray-500">Extending your session…</p>;
        }
        if (decision.kind === 'warn') {
            const remainingMinutes = Math.floor(remainingSeconds / 60);
            const remainingRemainderSeconds = remainingSeconds % 60;
            const remainingLabel = remainingMinutes > 0
                ? `${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''} and ${remainingRemainderSeconds} second${remainingRemainderSeconds !== 1 ? 's' : ''}`
                : `${remainingRemainderSeconds} second${remainingRemainderSeconds !== 1 ? 's' : ''}`;
            const inactiveMinutes = Math.floor(inactiveSeconds / 60);
            const inactiveRemainderSeconds = inactiveSeconds % 60;
            const inactiveLabel = inactiveMinutes > 0
                ? `${inactiveMinutes} minute${inactiveMinutes !== 1 ? 's' : ''} and ${inactiveRemainderSeconds} second${inactiveRemainderSeconds !== 1 ? 's' : ''}`
                : `${inactiveRemainderSeconds} second${inactiveRemainderSeconds !== 1 ? 's' : ''}`;
            return (
                <p>
                    You have been inactive for {inactiveLabel}. For your security, you will be signed out in{' '}
                    <strong>{`${remainingMinutes}:${remainingRemainderSeconds.toString().padStart(2, '0')}`}</strong>{' '}
                    ({remainingLabel}) unless you stay signed in.
                </p>
            );
        }
        return null;
    };

    return createPortal(
        <div
            role="alertdialog"
            aria-live="assertive"
            aria-labelledby="idle-warning-title"
            className={`fixed bottom-[10px] right-[80px] w-[380px] box-border rounded-md pointer-events-auto ${TOAST_Z_CLASS} bg-white shadow-lg overflow-hidden`}
            style={{ zIndex: TOAST_Z_INDEX }}
        >
            <div
                id="idle-warning-title"
                className="bg-yellow-500 text-white font-semibold p-2"
            >
                Idle Timeout Warning
            </div>
            <div className="bg-white text-black p-3 text-sm">
                {bodyContent()}
            </div>
            <div className="flex flex-row justify-end gap-2 p-2 bg-white">
                <Button
                    type="button"
                    size="sm"
                    style="solid"
                    color="subdued"
                    background="default"
                    className="cursor-pointer text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-sm px-3 py-2 text-sm"
                    onClick={() => {
                        resetAppNavSession(window.location.pathname || '/');
                        window.location.replace('/api/sign-out');
                    }}
                >
                    Sign out now
                </Button>
                <Button
                    type="button"
                    size="sm"
                    style="solid"
                    color="accent"
                    background="default"
                    disabled={extendPending}
                    className="cursor-pointer text-white bg-yellow-500 hover:bg-yellow-600 disabled:opacity-60 disabled:cursor-not-allowed rounded-sm px-3 py-2 text-sm"
                    onClick={handleStaySignedIn}
                >
                    {extendPending ? 'Extending…' : extendError ? 'Retry' : 'Stay signed in'}
                </Button>
            </div>
        </div>,
        layer,
    );
}
